/**
 * Daily dispatch email settings (iter301).
 *
 * Push notifications aren't configured, so this is how techs learn what
 * they're on. The platform cron fires hourly; the backend only sends on
 * the hour picked here, which means the send time is editable without a
 * redeploy.
 */
import { useEffect, useState } from "react";
import { tr } from "@/i18n";
import { toast } from "sonner";
import { Mail, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CollapsibleSection, Callout } from "@/components/settings/SettingsUI";
import { getDispatchEmailConfig, setDispatchEmailConfig, sendDispatchEmail } from "@/lib/api";

const HOURS = Array.from({ length: 24 }, (_, h) => ({
  value: String(h),
  label: `${((h + 11) % 12) + 1}:00 ${h < 12 ? "AM" : "PM"}`,
}));

// Mon=0 … Sun=6, matching Python's weekday().
const DAYS = [
  { v: 0, s: "M", label: "Monday" }, { v: 1, s: "T", label: "Tuesday" },
  { v: 2, s: "W", label: "Wednesday" }, { v: 3, s: "T", label: "Thursday" },
  { v: 4, s: "F", label: "Friday" }, { v: 5, s: "S", label: "Saturday" },
  { v: 6, s: "S", label: "Sunday" },
];

export default function DispatchEmailPanel() {
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [sending, setSending] = useState(false);

  const load = () => getDispatchEmailConfig().then(r => setCfg(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async (patch) => {
    setBusy(true);
    try {
      const { data } = await setDispatchEmailConfig(patch);
      setCfg(data);
      toast.success("Saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally { setBusy(false); }
  };

  const sendNow = async () => {
    if (!window.confirm("Email every tech their schedule for today right now?")) return;
    setSending(true);
    try {
      const { data } = await sendDispatchEmail({});
      toast.success(`${data.sent} sent${data.skipped ? ` · ${data.skipped} skipped` : ""}${data.failed ? ` · ${data.failed} failed` : ""}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally { setSending(false); }
  };

  if (!cfg) return null;
  const last = cfg.last_run_result;
  const days = cfg.send_days || [0, 1, 2, 3, 4, 5, 6];
  const toggleDay = (v) => {
    const next = days.includes(v) ? days.filter(d => d !== v) : [...days, v].sort();
    if (!next.length) { toast.error("Pick at least one day"); return; }
    save({ send_days: next });
  };

  return (
    <CollapsibleSection
      id="dispatch-email" testId="dispatch-email-section"
      icon={Mail} iconClass="text-sky-600" title="Daily schedule email to techs"
      extra={
        <span className={`text-[10px] px-1.5 py-0.5 rounded-full border font-semibold ${
          cfg.enabled ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-100 text-slate-500 border-slate-200"}`}
              data-testid="dispatch-email-state">
          {cfg.enabled ? "ON" : "OFF"}
        </span>
      }
      actions={
        <Button size="sm" variant="outline" onClick={sendNow} disabled={sending} data-testid="btn-send-dispatch-email-now">
          <Send className="w-3.5 h-3.5 mr-1.5" />{sending ? "Sending…" : "Send now"}
        </Button>
      }
    >
      <div className="space-y-3">
        <Callout tone="info">
          Each tech gets one email listing only their own jobs — work order, customer, site address
          with a maps link, site contact, hours and every dispatch note the office left. Techs with
          nothing scheduled get nothing. Nobody is emailed twice for the same day.
        </Callout>

        <label className="flex items-start gap-2 text-sm cursor-pointer" data-testid="toggle-dispatch-email">
          <input
            type="checkbox" checked={cfg.enabled} disabled={busy}
            onChange={(e) => save({ enabled: e.target.checked })}
            className="mt-0.5 h-4 w-4 accent-slate-900"
            data-testid="toggle-dispatch-email-input"
          />
          <span>
            <span className="font-medium">Send automatically every day</span>
            <span className="block text-xs text-slate-500">
              Leave this off and the Send now button (and the Email schedule button on the Dispatch board) still work.
            </span>
          </span>
        </label>

        <div className="flex flex-wrap gap-4">
          <div className="space-y-1">
            <Label className="text-xs">{tr("Send at")}</Label>
            <Select value={String(cfg.send_hour_local)} onValueChange={(v) => save({ send_hour_local: Number(v) })}>
              <SelectTrigger className="h-9 w-36 text-sm" data-testid="select-dispatch-email-hour"><SelectValue /></SelectTrigger>
              <SelectContent>{HOURS.map(h => <SelectItem key={h.value} value={h.value}>{h.label}</SelectItem>)}</SelectContent>
            </Select>
            <div className="text-[11px] text-slate-400">{cfg.timezone}</div>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Which Day</Label>
            <Select value={String(cfg.day_offset)} onValueChange={(v) => save({ day_offset: Number(v) })}>
              <SelectTrigger className="h-9 w-44 text-sm" data-testid="select-dispatch-email-day"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="0">Today's schedule</SelectItem>
                <SelectItem value="1">Tomorrow's schedule</SelectItem>
              </SelectContent>
            </Select>
            <div className="text-[11px] text-slate-400">
              Pick tomorrow if you send in the evening
            </div>
          </div>
        </div>

        <div className="space-y-1.5 border-t border-slate-100 pt-3">
          <Label className="text-xs">Which Days</Label>
          <div className="flex items-center gap-1.5 flex-wrap" data-testid="dispatch-email-days">
            {DAYS.map(d => {
              const on = days.includes(d.v);
              return (
                <button
                  key={d.v} type="button" title={d.label} disabled={busy}
                  onClick={() => toggleDay(d.v)}
                  data-testid={`dispatch-email-day-${d.v}`}
                  aria-pressed={on}
                  className={`w-9 h-9 rounded-full text-sm font-semibold border transition-colors ${
                    on ? "bg-slate-900 text-white border-slate-900"
                       : "bg-white text-slate-400 border-slate-200 hover:border-slate-400"}`}
                >
                  {d.s}
                </button>
              );
            })}
            <Button size="sm" variant="ghost" className="h-8 text-xs text-slate-500"
                    disabled={busy} onClick={() => save({ send_days: [0, 1, 2, 3, 4] })}
                    data-testid="btn-dispatch-email-weekdays">
              Weekdays only
            </Button>
            <Button size="sm" variant="ghost" className="h-8 text-xs text-slate-500"
                    disabled={busy} onClick={() => save({ send_days: [0, 1, 2, 3, 4, 5, 6] })}
                    data-testid="btn-dispatch-email-everyday">
              Every day
            </Button>
          </div>
          <div className="text-[11px] text-slate-400">
            Days refer to the <strong>schedule</strong> being emailed
            {cfg.day_offset === 1 ? " — with \"tomorrow's schedule\" picked, the Friday evening send covers Saturday" : ""}.
            Turn Saturday and Sunday off and nothing goes out for the weekend.
          </div>
        </div>

        {last && (
          <div className="text-xs text-slate-500 border-t border-slate-100 pt-2" data-testid="dispatch-email-last-run">
            Last run {cfg.last_run_at ? new Date(cfg.last_run_at).toLocaleString() : "—"} for {last.date}:
            <strong className="text-slate-700"> {last.sent} sent</strong>
            {last.skipped ? `, ${last.skipped} skipped` : ""}
            {last.failed ? `, ${last.failed} failed` : ""}
          </div>
        )}
      </div>
    </CollapsibleSection>
  );
}
